// P5/B2-frontend — the effective-permissions role-gating hook (ADR-017).
//
// Fetches GET /v1/me/effective-permissions (cache-served by the server from the
// caller's resolved bitset) and exposes it as a capability Set the <ScopeGate>
// component + the rail filter consume. The capability vocabulary is the SAME
// `<module>.<action>` string the menu-config requiredCapability fields use (e.g.
// "people.read", "payroll.approve") so the rail filter is a direct membership
// test.
//
// ⚠ ADVISORY ONLY (ADR-008/ADR-017): this hook decides what the UI SHOWS, never
// what the caller may DO — the server re-checks every request. A stale/forged
// capability set cannot widen actual authority; it only dangles a button that
// then 403s.
//
// MODULE-KEY NOTE: effective-permissions returns the CATALOG module keys (the
// 17 iston modules: requests/hris/payroll/…). The menu-config requiredCapability
// fields predate the matrix and use a few LEGACY synonyms (people→hris,
// org→hris, time→time_attendance, tasks→requests).
// `capabilityMatches` resolves those synonyms so a menu entry tagged
// "people.read" is satisfied by the catalog cell hris.read.

import { useQuery } from "@tanstack/react-query";

import {
  fetchEffectivePermissions,
  type EffectiveCell,
} from "./api";

/** A `<module>.<action>` capability string (the ScopeGate vocabulary). */
export type Capability = string;

/**
 * Legacy menu-capability module → catalog module synonym. The menu-config uses
 * pre-matrix module names; the matrix uses the catalog keys. One small map
 * reconciles them so the rail filter + ScopeGate read ONE capability set.
 *
 * An entry is only ever a bridge for a name the catalog does NOT have. Once the
 * catalog gains a module under its own name, its entry here must go — otherwise
 * the menu keeps resolving to the old cell while the routes move to the new one,
 * and the item disappears for a population nobody chose. menu-capability-
 * catalog.test.ts enforces exactly that. `assets` was removed when migration
 * 0225 gave asset custody a native module (SEM-6958), `benefits` when 0236
 * moved the last seven benefits route cells off `hris` (SEM-7084).
 */
const MODULE_SYNONYM: Readonly<Record<string, string>> = {
  people: "hris",
  org: "hris",
  time: "time_attendance",
  tasks: "requests",
  // The plural menu vocabulary must bridge to singular "survey" or default-closed matching hides the item.
  surveys: "survey",
  leave: "leave",
  onboarding: "onboarding",
  reports: "reports",
  settings: "settings",
  requests: "requests",
};

/** Exported for the menu-capability-catalog drift guard. */
export function canonicalModule(module: string): string {
  return MODULE_SYNONYM[module] ?? module;
}

/**
 * Build the allowed-capability Set from the resolved cells: one
 * `<module>.<action>` entry per allowed=true cell.
 */
export function cellsToCapabilitySet(
  cells: readonly EffectiveCell[],
): ReadonlySet<Capability> {
  const set = new Set<Capability>();
  for (const c of cells) {
    if (c.allowed) set.add(`${c.module}.${c.action}`);
  }
  return set;
}

/**
 * Does the caller's resolved capability set satisfy `required`? Resolves the
 * required capability's module through the legacy-synonym map first, so a menu
 * entry tagged "people.read" matches the catalog cell hris.read. Default-closed:
 * an unparseable / absent capability returns false.
 */
export function capabilityMatches(
  allowed: ReadonlySet<Capability>,
  required: Capability,
): boolean {
  const dot = required.lastIndexOf(".");
  if (dot <= 0) return false;
  const module = canonicalModule(required.slice(0, dot));
  const action = required.slice(dot + 1);
  return allowed.has(`${module}.${action}`);
}

export interface EffectivePermissionsState {
  readonly capabilities: ReadonlySet<Capability>;
  readonly sourceProfileIds: readonly string[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * React hook: fetch + expose the caller's effective permissions as a capability
 * Set. While loading / on error the set is EMPTY (default-closed — the UI hides
 * gated actions until the real set resolves, never flashes them).
 */
export function useEffectivePermissions(): EffectivePermissionsState {
  const query = useQuery({
    queryKey: ["me", "effective-permissions"],
    queryFn: fetchEffectivePermissions,
    // The resolved bitset is server-cached (≤30s TTL); a 1-minute client stale
    // window is well within the kill-switch budget and avoids a refetch storm.
    staleTime: 60_000,
  });

  const capabilities =
    query.data !== undefined
      ? cellsToCapabilitySet(query.data.permissions)
      : new Set<Capability>();

  return {
    capabilities,
    sourceProfileIds: query.data?.source_profile_ids ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
